import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Box, Container, Typography, Button, Paper, Tabs, Tab, 
    List, ListItem, ListItemIcon, ListItemText, Chip, Divider, 
    useMediaQuery, useTheme, ToggleButtonGroup, ToggleButton, Grid
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useAuth } from '../../../context/AuthContext'; 
import api from '../../../services/api'; 

const TIERS = [
    {
        id: 'INCUBATOR',
        name: 'Incubator',
        monthlyPrice: 'R0 Base',
        annualPrice: 'R0 Base',
        suffix: '+ R85 / student',
        subtitle: 'Pay-As-You-Go',
        description: 'Perfect for self-funded sponsors, small trusts, and first-time CSI initiatives.',
        // We removed the hardcoded limit lines from the features array, we will inject them dynamically below!
        features: [
            'Applicant Smart Profiles',
            'Digital Vault (ID, Proof of Address)',
            'Basic Application Tracking',
            'Manual Document Verification'
        ]
    },
    {
        id: 'STARTER',
        name: 'Starter',
        monthlyPrice: 'R2,500',
        annualPrice: 'R25,000',
        suffix: '/ month',
        savings: 'Save R5,000',
        subtitle: 'The Essentials',
        description: 'Predictable retainer for established NGOs, municipalities, and medium family trusts.',
        features: [
            'Everything in Incubator',
            'Custom Scoring Rubrics',
            'Bulk Actions (Accept/Reject/Email)',
            'Export to CSV (Basic Reporting)'
        ]
    },
    {
        id: 'GROWTH',
        name: 'Growth',
        monthlyPrice: 'R9,500',
        annualPrice: 'R95,000',
        suffix: '/ month',
        savings: 'Save R19,000',
        subtitle: 'The Professional',
        description: 'Fixed operational budgets for Corporate CSI, large foundations, and university offices.',
        features: [
            'Everything in Starter',
            'Automated ID & Bank Verification',
            'B-BBEE Compliance Reporting',
            'Advanced System Settings',
            'Priority Support & Custom Branding'
        ],
        isPopular: true
    },
    {
        id: 'ENTERPRISE',
        name: 'Enterprise',
        monthlyPrice: 'R15,000+',
        annualPrice: 'R150,000+',
        suffix: '/ month',
        subtitle: 'The Scale',
        description: 'Custom SLAs for mining houses, banks, and national government funds.',
        features: [
            'Custom API Integrations (SAP/Sage)',
            'Dedicated Account Manager',
            'Customized Legal SLA'
        ]
    }
];

const PricingPage = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const { user, token } = useAuth();
    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
    
    const [activeTab, setActiveTab] = useState(2); 
    const [billingCycle, setBillingCycle] = useState('annual');
    const [currentTier, setCurrentTier] = useState(null);
    const [limits, setLimits] = useState([]);

    useEffect(() => {
        // Fetch dynamic limits from the database (Ensuring your public route exists!)
        api.get('/public/tier-limits')
        .then(res => setLimits(res.data))
        .catch(err => console.error("Could not fetch tier limits:", err));
    }, []);

    // Helper function to extract a specific limit
    const getLimit = (tierPrefix, type, fallback) => {
        const setting = limits.find(s => s.SettingKey === `${tierPrefix}_max_${type}`);
        if (!setting) return fallback;
        return setting.SettingValue === '-1' ? 'Unlimited' : setting.SettingValue;
    };

    useEffect(() => {
        if (user && token && ['Admin', 'SuperUser', 'Finance'].includes(user.role)) {
            api.get('/company/my-company', { headers: { Authorization: `Bearer ${token}` } }) 
               .then(res => {
                   setCurrentTier(res.data.SubscriptionTier?.toUpperCase());
               })
               .catch(err => console.error("Failed to fetch current tier:", err));
        }
    }, [user, token]);

    const handleSelectPlan = (tierId) => {
        if (user) {
            navigate(`/funder/billing?upgradeTier=${tierId}&cycle=${billingCycle}`);
        } else {
            navigate(`/auth?mode=register&type=Funder&tier=${tierId}`);
        }
    };

    const getSuffix = (tier) => {
        if (tier.id === 'INCUBATOR') return tier.suffix;
        return billingCycle === 'annual' ? '/ year' : '/ month';
    };

    const activeMobileTier = TIERS[activeTab];
    const isMobileCurrentPlan = activeMobileTier.id === currentTier;

    // Helper component to render the dynamic limits + the hardcoded features cleanly
    const FeatureList = ({ tier }) => {
        const tierPrefix = `tier_${tier.id.toLowerCase()}`;
        return (
            <List dense sx={{ mb: 0 }}>
                {/* 1. Dynamic Admin Limit */}
                <ListItem disableGutters alignItems="flex-start">
                    <ListItemIcon sx={{ minWidth: 28, mt: 0.5 }}><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                    <ListItemText primary={`${getLimit(tierPrefix, 'users', '1')} Admin Accounts`} primaryTypographyProps={{ fontSize: '0.9rem', lineHeight: 1.3, fontWeight: 'bold' }} />
                </ListItem>
                
                {/* 2. Dynamic Student Limit */}
                <ListItem disableGutters alignItems="flex-start">
                    <ListItemIcon sx={{ minWidth: 28, mt: 0.5 }}><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                    <ListItemText primary={`${getLimit(tierPrefix, 'apps', '100')} Active Students`} primaryTypographyProps={{ fontSize: '0.9rem', lineHeight: 1.3, fontWeight: 'bold' }} />
                </ListItem>

                 {/* 3. Dynamic Funds Limit */}
                 <ListItem disableGutters alignItems="flex-start">
                    <ListItemIcon sx={{ minWidth: 28, mt: 0.5 }}><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                    <ListItemText primary={`${getLimit(tierPrefix, 'funds', '1')} Active Bursary Funds`} primaryTypographyProps={{ fontSize: '0.9rem', lineHeight: 1.3, fontWeight: 'bold' }} />
                </ListItem>

                <Divider sx={{ my: 1 }} />

                {/* 4. The rest of the hardcoded features */}
                {tier.features.map((feat, i) => (
                    <ListItem key={i} disableGutters alignItems="flex-start">
                        <ListItemIcon sx={{ minWidth: 28, mt: 0.5 }}><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                        <ListItemText primary={feat} primaryTypographyProps={{ fontSize: '0.9rem', lineHeight: 1.3 }} />
                    </ListItem>
                ))}
            </List>
        );
    };

    return (
        <Container maxWidth="xl" sx={{ py: 8 }}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
                <Typography variant="h3" fontWeight="bold" gutterBottom>
                    Scalable Pricing for South African Funders
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
                    From community trusts to national corporate CSI. Start your 30-Day Free Trial today.
                </Typography>

                <ToggleButtonGroup
                    value={billingCycle}
                    exclusive
                    onChange={(e, newCycle) => { if (newCycle) setBillingCycle(newCycle); }}
                    color="primary"
                    sx={{ bgcolor: 'background.paper', boxShadow: 1 }}
                >
                    <ToggleButton value="monthly" sx={{ px: 4, fontWeight: 'bold' }}>Pay Monthly</ToggleButton>
                    <ToggleButton value="annual" sx={{ px: 4, fontWeight: 'bold' }}>
                        Annual Upfront <Chip label="Save up to 2 Months" color="success" size="small" sx={{ ml: 1, height: 20 }} />
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {isMobile ? (
                <Paper elevation={3} sx={{ borderRadius: 3, overflow: 'hidden', maxWidth: 500, mx: 'auto' }}>
                    <Tabs value={activeTab} onChange={(e, val) => setActiveTab(val)} variant="scrollable" scrollButtons="auto" indicatorColor="primary" textColor="primary">
                        {TIERS.map((tier, idx) => <Tab key={tier.id} label={tier.name} value={idx} sx={{ fontWeight: 'bold' }} />)}
                    </Tabs>
                    <Box sx={{ p: 4 }}>
                        <Typography variant="subtitle2" color="primary" fontWeight="bold" sx={{ textTransform: 'uppercase' }}>{activeMobileTier.subtitle}</Typography>
                        
                        <Typography variant="h4" fontWeight="bold" gutterBottom>
                            {billingCycle === 'annual' ? activeMobileTier.annualPrice : activeMobileTier.monthlyPrice}
                            <Typography component="span" variant="body1" color="text.secondary"> {getSuffix(activeMobileTier)}</Typography>
                        </Typography>
                        
                        {billingCycle === 'annual' && activeMobileTier.savings && (
                            <Chip label={activeMobileTier.savings} color="success" variant="outlined" sx={{ mb: 2, fontWeight: 'bold' }} />
                        )}

                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>{activeMobileTier.description}</Typography>
                        
                        <Button 
                            variant="contained" 
                            fullWidth 
                            size="large" 
                            disabled={isMobileCurrentPlan}
                            onClick={() => handleSelectPlan(activeMobileTier.id)} 
                            sx={{ mb: 3, py: 1.5, fontWeight: 'bold' }}
                        >
                            {isMobileCurrentPlan ? 'Current Plan' : activeMobileTier.id === 'ENTERPRISE' ? 'Contact Sales' : 'Activate Plan'}
                        </Button>

                        <Divider sx={{ mb: 2 }} />
                        
                        {/* Render Mobile Feature List */}
                        <FeatureList tier={activeMobileTier} />
                        
                    </Box>
                </Paper>
            ) : (
                <Grid container spacing={3} justifyContent="center" alignItems="stretch">
                {TIERS.map((tier) => {
                    const isCurrentPlan = tier.id.toUpperCase() === currentTier;

                        return (
                            <Grid item xs={12} sm={6} lg={3} key={tier.id} sx={{ display: 'flex' }}>
                                <Paper 
                                    elevation={tier.isPopular ? 8 : 2} 
                                    sx={{ 
                                        flex: 1, p: 4, borderRadius: 3, position: 'relative', display: 'flex', flexDirection: 'column',
                                        border: tier.isPopular ? `2px solid ${theme.palette.primary.main}` : '1px solid #eee',
                                        transform: tier.isPopular ? 'scale(1.03)' : 'scale(1)', zIndex: tier.isPopular ? 2 : 1
                                    }}
                                >
                                    {tier.isPopular && <Chip label="Most Popular" color="primary" sx={{ position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)', fontWeight: 'bold' }} />}
                                    
                                    <Typography variant="subtitle2" color="primary" fontWeight="bold" sx={{ textTransform: 'uppercase', mb: 1 }}>{tier.subtitle}</Typography>
                                    <Typography variant="h5" fontWeight="bold" gutterBottom>{tier.name}</Typography>
                                    
                                    <Typography variant="h4" fontWeight="bold" sx={{ mb: 1, minHeight: 80 }}>
                                        {billingCycle === 'annual' ? tier.annualPrice : tier.monthlyPrice}
                                        <Typography component="span" variant="body1" color="text.secondary" display="block"> {getSuffix(tier)}</Typography>
                                    </Typography>

                                    <Box sx={{ height: 32, mb: 2 }}>
                                        {billingCycle === 'annual' && tier.savings && <Chip label={tier.savings} color="success" variant="outlined" size="small" sx={{ fontWeight: 'bold' }} />}
                                    </Box>

                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 4, flexGrow: 1 }}>{tier.description}</Typography>
                                    
                                    <Button 
                                        variant={tier.isPopular ? "contained" : "outlined"} 
                                        fullWidth 
                                        size="large" 
                                        disabled={isCurrentPlan} 
                                        onClick={() => handleSelectPlan(tier.id)} 
                                        sx={{ mb: 4, fontWeight: 'bold' }}
                                    >
                                        {isCurrentPlan ? 'Current Plan Active' : tier.id === 'ENTERPRISE' ? 'Contact Sales' : 'Activate Plan'}
                                    </Button>

                                    <Divider sx={{ mb: 2 }} />

                                    {/* Render Desktop Feature List */}
                                    <FeatureList tier={tier} />

                                </Paper>
                            </Grid>
                        );
                    })}
                </Grid>
            )}
        </Container>
    );
};

export default PricingPage;